/**
 * WorkspaceRoleBadge - Centralized component for displaying workspace collaborator roles.
 * Use this component across all pages to ensure consistent styling.
 */

import { Crown, Shield, Pencil, Eye, HardHat, UserX, Ban, type LucideIcon } from "lucide-react";
import type { WorkspaceCollaboratorRole } from "../services/naraDoor";

interface RoleConfig {
  icon: LucideIcon;
  label: string;
  bgColor: string;
  textColor: string;
  borderColor?: string;
}

// Shown for a seat with no role, or a role this table does not know.
const NO_ROLE_CONFIG: RoleConfig = {
  icon: UserX,
  label: "None",
  bgColor: "bg-muted",
  textColor: "text-muted-foreground",
  borderColor: "border-border",
};

const ROLE_CONFIG: Record<WorkspaceCollaboratorRole, RoleConfig> = {
  owner: {
    icon: Crown,
    label: "Owner",
    bgColor: "bg-yellow-100 dark:bg-yellow-900",
    textColor: "text-yellow-700 dark:text-yellow-300",
  },
  admin: {
    icon: Shield,
    label: "Admin",
    bgColor: "bg-blue-100 dark:bg-blue-900",
    textColor: "text-blue-700 dark:text-blue-300",
  },
  editor: {
    icon: Pencil,
    label: "Editor",
    bgColor: "bg-green-100 dark:bg-green-900",
    textColor: "text-green-700 dark:text-green-300",
  },
  viewer: {
    icon: Eye,
    label: "Viewer",
    bgColor: "bg-purple-100 dark:bg-purple-900",
    textColor: "text-purple-700 dark:text-purple-300",
  },
  uploader: {
    icon: HardHat,
    label: "Field Agent",
    bgColor: "bg-orange-100 dark:bg-orange-900",
    textColor: "text-orange-700 dark:text-orange-300",
  },
  inactive: {
    icon: Ban,
    label: "Inactive",
    bgColor: "bg-red-100 dark:bg-red-900",
    textColor: "text-red-700 dark:text-red-300",
  },
};

const ROLE_DESCRIPTIONS: Record<WorkspaceCollaboratorRole, string> = {
  owner: "Full access to all workspace settings, billing, and member management. Can delete the workspace.",
  admin: "Can manage projects, collaborators, and most workspace settings. Cannot access billing or delete workspace.",
  editor: "Can edit and contribute to projects they are assigned to. Cannot manage workspace settings.",
  viewer: "Read-only access to projects they are assigned to. Cannot make changes.",
  uploader: "Can upload recordings to the projects they are assigned to. Cannot see anything else.",
  inactive: "Account is disabled. All access is blocked.",
};

interface WorkspaceRoleBadgeProps {
  role?: WorkspaceCollaboratorRole | null;
  size?: "sm" | "md";
  showIcon?: boolean;
  className?: string;
}

export function WorkspaceRoleBadge({
  role,
  size = "sm",
  showIcon = true,
  className = "",
}: WorkspaceRoleBadgeProps) {
  const config = (role && ROLE_CONFIG[role]) || NO_ROLE_CONFIG;
  const Icon = config.icon;

  const sizeClasses = size === "sm"
    ? "px-2 py-0.5 text-xs"
    : "px-2.5 py-1 text-sm";

  const iconSize = size === "sm" ? "h-3 w-3" : "h-4 w-4";

  // Use actual role label if config doesn't match (for unknown roles)
  const label = role && ROLE_CONFIG[role] ? config.label : role || NO_ROLE_CONFIG.label;

  return (
    <span
      className={`inline-flex items-center gap-1 font-medium rounded-full ${config.bgColor} ${config.textColor} ${config.borderColor ? `border ${config.borderColor}` : ""} ${sizeClasses} ${className}`}
    >
      {showIcon && <Icon className={iconSize} />}
      {label}
    </span>
  );
}

export function getRoleLabel(role: WorkspaceCollaboratorRole): string {
  return ROLE_CONFIG[role]?.label || role;
}

export function getRoleDescription(role: WorkspaceCollaboratorRole): string {
  return ROLE_DESCRIPTIONS[role] || "";
}

export function getRoleConfig(role: WorkspaceCollaboratorRole): RoleConfig {
  return ROLE_CONFIG[role];
}

export { ROLE_CONFIG, ROLE_DESCRIPTIONS };
